import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  LogOut, Mail, Phone, MapPin, Trash2, Search, RefreshCw, ChevronDown,
  Download, DollarSign, Check, Camera,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const STATUSES = [
  { id: "new", label: "New", color: "bg-[#FF5A00] text-black" },
  { id: "contacted", label: "Contacted", color: "bg-[#F59E0B] text-black" },
  { id: "quoted", label: "Quoted", color: "bg-[#3B82F6] text-white" },
  { id: "won", label: "Won", color: "bg-[#10B981] text-black" },
  { id: "lost", label: "Lost", color: "bg-[#666] text-white" },
];

const FENCE_LABEL = {
  timber: "Timber",
  colorbond: "Colorbond",
  chain_link: "Chain Link",
  aluminium: "Aluminium",
  wrought_iron: "Wrought Iron",
  other: "Other",
};

const TIMELINE_LABEL = {
  asap: "ASAP",
  "1_month": "≤ 1 month",
  "1_3_months": "1–3 months",
  flexible: "Flexible",
};

function statusBadge(status) {
  const s = STATUSES.find((x) => x.id === status) || STATUSES[0];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-bold ${s.color}`}
    >
      {s.label}
    </span>
  );
}

function formatAUD(n) {
  if (n == null || n === "") return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", maximumFractionDigits: 0,
  }).format(Number(n));
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [amountDraft, setAmountDraft] = useState("");
  const [savingAmount, setSavingAmount] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (selected) {
      setAmountDraft(selected.quoted_amount != null ? String(selected.quoted_amount) : "");
    }
  }, [selected]);

  const filtered = useMemo(() => {
    let list = leads;
    if (filter !== "all") list = list.filter((l) => l.status === filter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          l.email.toLowerCase().includes(q) ||
          l.phone.toLowerCase().includes(q) ||
          l.suburb.toLowerCase().includes(q)
      );
    }
    return list;
  }, [leads, query, filter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveAmount = async () => {
    if (!selected) return;
    const num = amountDraft === "" ? null : Number(amountDraft);
    if (num !== null && (Number.isNaN(num) || num < 0)) {
      toast.error("Enter a valid amount");
      return;
    }
    setSavingAmount(true);
    try {
      const { data } = await api.patch(`/admin/leads/${selected.id}`, { quoted_amount: num });
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      setSelected(data);
      toast.success("Quoted amount saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingAmount(false);
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("td_token");
      const res = await fetch(`${API}/admin/leads.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const filename = res.headers.get("Content-Disposition")?.match(/filename="?([^";]+)"?/i)?.[1]
        || `tradesmandan_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${leads.length} leads`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const statCards = stats
    ? [
        { label: "Total", value: stats.total, accent: "text-white" },
        { label: "New", value: stats.new, accent: "text-[#FF5A00]" },
        { label: "Contacted", value: stats.contacted, accent: "text-[#F59E0B]" },
        { label: "Quoted", value: stats.quoted, accent: "text-[#3B82F6]" },
        { label: "Won", value: stats.won, accent: "text-[#10B981]" },
        { label: "Lost", value: stats.lost, accent: "text-[#A3A3A3]" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      {/* Top bar */}
      <header className="border-b border-[#333] bg-[#0D0D0D] sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#FF5A00] flex items-center justify-center font-heading text-black text-xl">
              TD
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF5A00] font-bold">
                Control Room
              </div>
              <div className="font-heading text-2xl uppercase tracking-tight">
                Leads Dashboard
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="dashboard-site-link"
              className="hidden sm:block text-xs tracking-[0.3em] uppercase text-white/60 hover:text-[#FF5A00]"
            >
              View Site
            </Link>
            <div className="hidden md:block text-xs tracking-[0.2em] uppercase text-[#A3A3A3]">
              {user?.email}
            </div>
            <button
              data-testid="dashboard-logout"
              onClick={logout}
              className="flex items-center gap-2 border border-[#333] hover:border-[#FF5A00] px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10">
        {/* Status counts */}
        <div data-testid="dashboard-stats" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#333] mb-6">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#0D0D0D] p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                {s.label}
              </div>
              <div className={`font-heading text-5xl mt-1 ${s.accent}`}>
                {s.value}
              </div>
            </div>
          ))}
          {!stats &&
            Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-[#0D0D0D] p-5 h-24 animate-pulse" />
            ))}
        </div>

        {/* Revenue stats */}
        {stats && (
          <div data-testid="dashboard-revenue" className="grid sm:grid-cols-2 gap-px bg-[#333] mb-10">
            <div className="bg-[#0D0D0D] p-6 flex items-center gap-5">
              <div className="w-12 h-12 bg-[#10B981] flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-black" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                  Won Value
                </div>
                <div className="font-heading text-3xl text-[#10B981] mt-1">
                  {formatAUD(stats.won_value)}
                </div>
              </div>
            </div>
            <div className="bg-[#0D0D0D] p-6 flex items-center gap-5">
              <div className="w-12 h-12 bg-[#3B82F6] flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-white" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                  Pipeline Value · Contacted + Quoted
                </div>
                <div className="font-heading text-3xl text-[#3B82F6] mt-1">
                  {formatAUD(stats.pipeline_value)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
            <Input
              data-testid="leads-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone, suburb…"
              className="pl-9 bg-[#1A1A1A] border-[#333] rounded-none text-white focus:border-[#FF5A00] h-11"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[{ id: "all", label: "All" }, ...STATUSES].map((s) => (
              <button
                key={s.id}
                data-testid={`leads-filter-${s.id}`}
                onClick={() => setFilter(s.id)}
                className={`px-4 h-11 text-xs tracking-[0.2em] uppercase font-bold transition-all ${
                  filter === s.id
                    ? "bg-[#FF5A00] text-black"
                    : "bg-transparent text-white/70 border border-[#333] hover:border-[#FF5A00]"
                }`}
              >
                {s.label}
              </button>
            ))}
            <button
              data-testid="leads-export-csv"
              onClick={exportCsv}
              className="px-4 h-11 border border-[#333] hover:border-[#10B981] hover:text-[#10B981] text-white/80 flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              data-testid="leads-refresh"
              onClick={load}
              className="px-4 h-11 border border-[#333] hover:border-[#FF5A00] text-white/70 hover:text-[#FF5A00] flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="border border-[#333] overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-left">
            <thead className="bg-[#1A1A1A] border-b border-[#333]">
              <tr className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                <th className="px-4 py-4">Name</th>
                <th className="px-4 py-4 hidden md:table-cell">Contact</th>
                <th className="px-4 py-4 hidden lg:table-cell">Project</th>
                <th className="px-4 py-4 hidden lg:table-cell">When</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4 hidden xl:table-cell">Quoted $</th>
                <th className="px-4 py-4 hidden md:table-cell">Received</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-[#666] tracking-[0.3em] uppercase text-xs">
                    Loading leads…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-[#666] tracking-[0.3em] uppercase text-xs">
                    No leads match
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    className="border-b border-[#222] hover:bg-[#1A1A1A] cursor-pointer transition-colors"
                    onClick={() => setSelected(l)}
                  >
                    <td className="px-4 py-4">
                      <div className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                        {l.name}
                        {l.photos && l.photos.length > 0 && (
                          <span
                            data-testid={`row-photo-badge-${l.id}`}
                            className="inline-flex items-center gap-1 bg-[#FF5A00] text-black text-[10px] tracking-wider font-bold px-1.5 py-0.5"
                            title={`${l.photos.length} photo${l.photos.length > 1 ? 's' : ''}`}
                          >
                            <Camera className="w-3 h-3" /> {l.photos.length}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#A3A3A3] flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {l.suburb}
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell text-sm">
                      <div className="text-white flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#FF5A00]" />{l.email}</div>
                      <div className="text-[#A3A3A3] flex items-center gap-2 mt-1"><Phone className="w-3.5 h-3.5 text-[#FF5A00]" />{l.phone}</div>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <div className="font-heading uppercase tracking-tight text-base">
                        {FENCE_LABEL[l.fence_type]}
                      </div>
                      <div className="text-xs text-[#A3A3A3] mt-1">{l.length_meters}m</div>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell text-sm text-[#D4D4D4]">
                      {TIMELINE_LABEL[l.timeline]}
                    </td>
                    <td className="px-4 py-4">
                      <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`status-trigger-${l.id}`}
                              className="flex items-center gap-2 group"
                            >
                              {statusBadge(l.status)}
                              <ChevronDown className="w-3 h-3 text-[#666] group-hover:text-[#FF5A00]" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#0D0D0D] border-[#333] rounded-none text-white">
                            {STATUSES.map((s) => (
                              <DropdownMenuItem
                                key={s.id}
                                data-testid={`status-set-${l.id}-${s.id}`}
                                onClick={() => updateStatus(l.id, s.id)}
                                className="cursor-pointer focus:bg-[#FF5A00]/20 focus:text-white tracking-[0.15em] uppercase text-xs"
                              >
                                {s.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden xl:table-cell text-sm font-heading tracking-tight text-[#10B981]">
                      {l.quoted_amount != null ? formatAUD(l.quoted_amount) : <span className="text-[#666]">—</span>}
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell text-xs text-[#A3A3A3]">
                      {new Date(l.created_at).toLocaleString("en-AU", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`lead-delete-${l.id}`}
                        onClick={() => removeLead(l.id)}
                        className="p-2 text-[#666] hover:text-[#EF4444] transition-colors"
                        aria-label="Delete lead"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Selected lead drawer */}
        {selected && (
          <div
            data-testid="lead-detail-overlay"
            className="fixed inset-0 z-40 bg-black/70 flex justify-end"
            onClick={() => setSelected(null)}
          >
            <div
              className="w-full max-w-lg bg-[#0D0D0D] border-l border-[#333] h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-[#333] p-6 flex items-start justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF5A00] font-bold mb-2">
                    Lead Detail
                  </div>
                  <div className="font-heading text-3xl uppercase tracking-tight">
                    {selected.name}
                  </div>
                  <div className="mt-2">{statusBadge(selected.status)}</div>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-white/60 hover:text-white text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-5">
                <DetailRow label="Email" value={<a href={`mailto:${selected.email}`} className="text-[#FF5A00] underline break-all">{selected.email}</a>} />
                <DetailRow label="Phone" value={<a href={`tel:${selected.phone}`} className="text-[#FF5A00] underline">{selected.phone}</a>} />
                <DetailRow label="Suburb" value={selected.suburb} />
                <DetailRow label="Fence Type" value={FENCE_LABEL[selected.fence_type]} />
                <DetailRow label="Length" value={`${selected.length_meters} metres`} />
                <DetailRow label="Timeline" value={TIMELINE_LABEL[selected.timeline]} />
                <DetailRow
                  label="Message"
                  value={selected.message || <span className="text-[#666]">— No message —</span>}
                />

                {/* Customer photos */}
                {selected.photos && selected.photos.length > 0 && (
                  <div data-testid="lead-photos">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] flex items-center gap-2">
                        <Camera className="w-3.5 h-3.5 text-[#FF5A00]" />
                        Customer Photos ({selected.photos.length})
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {selected.photos.map((p, i) => (
                        <button
                          key={i}
                          data-testid={`lead-photo-${i}`}
                          onClick={() => setLightbox(p)}
                          className="relative aspect-square bg-[#1A1A1A] border border-[#333] hover:border-[#FF5A00] transition-colors overflow-hidden group"
                        >
                          <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-[#0D0D0D]/0 group-hover:bg-[#0D0D0D]/40 transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <DetailRow
                  label="Received"
                  value={new Date(selected.created_at).toLocaleString("en-AU")}
                />

                {/* Quoted amount editor */}
                <div className="pt-2 border-t border-[#333]">
                  <Label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                    Quoted Amount (AUD)
                  </Label>
                  <div className="mt-2 flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666] font-heading">$</span>
                      <Input
                        data-testid="lead-quoted-amount"
                        type="number"
                        min="0"
                        step="50"
                        value={amountDraft}
                        onChange={(e) => setAmountDraft(e.target.value)}
                        placeholder="0"
                        className="pl-7 bg-[#1A1A1A] border-[#333] rounded-none text-white focus:border-[#FF5A00] h-11"
                      />
                    </div>
                    <button
                      data-testid="lead-quoted-save"
                      onClick={saveAmount}
                      disabled={savingAmount}
                      className="bg-[#FF5A00] text-black font-heading tracking-[0.2em] uppercase px-5 hover:bg-[#E65100] disabled:opacity-50 flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" /> Save
                    </button>
                  </div>
                  {selected.quoted_amount != null && (
                    <div className="mt-2 text-xs text-[#10B981] tracking-wider">
                      Current: {formatAUD(selected.quoted_amount)}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-[#333] flex gap-2 flex-wrap">
                  {STATUSES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => updateStatus(selected.id, s.id)}
                      className={`px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold transition-all ${
                        selected.status === s.id
                          ? "bg-[#FF5A00] text-black"
                          : "border border-[#333] hover:border-[#FF5A00] text-white/70"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Lightbox for photo zoom */}
        {lightbox && (
          <div
            data-testid="lead-photo-lightbox"
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 text-white/80 hover:text-[#FF5A00] text-3xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={lightbox}
              alt="Customer photo"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{label}</div>
      <div className="font-body text-white text-base mt-1">{value}</div>
    </div>
  );
}
